import React, { useState, useEffect } from 'react';
import { collection, query, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import { Business, BusinessModel } from '../models';
import ProductDetailView from './ProductDetailView';

interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  imageUrl: string;
  businessId: string;
  createdAt: Date;
  updatedAt: Date;
}

interface BusinessDetailViewProps {
  businessId: string;
  isOpen: boolean;
  onClose: () => void;
}

const BusinessDetailView: React.FC<BusinessDetailViewProps> = ({ businessId, isOpen, onClose }) => {
  const [business, setBusiness] = useState<Business | null>(null);
  const [products, setProducts] = useState<Product[]>([]);
  const [loadingBusiness, setLoadingBusiness] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [businessRating, setBusinessRating] = useState(5.0);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [showProductDetail, setShowProductDetail] = useState(false);

  useEffect(() => {
    if (isOpen && businessId) {
      loadBusinessData();
      loadProducts();
      loadLikeStatus();
    }
  }, [isOpen, businessId]);

  const loadBusinessData = async () => {
    if (!businessId) return;

    setLoadingBusiness(true);
    try {
      // Fetch business data from Firestore
      const businessDoc = await getDoc(doc(db, 'businesses', businessId));
      if (businessDoc.exists()) {
        const data = businessDoc.data();
        const businessData: Business = {
          id: businessDoc.id,
          name: data.name || '',
          description: data.description || '',
          category: data.category || 'Services',
          location: data.location,
          imageUrl: data.imageUrl,
          phoneNumber: data.phoneNumber || '',
          createdAt: data.createdAt?.toDate() || new Date(),
          createdBy: data.createdBy || '',
          isActive: data.isActive || false,
          subscriptionValidUntil: data.subscriptionValidUntil?.toDate() || new Date(),
          likes: data.likes || 0,
          userLikes: data.userLikes || [],
          rating: data.rating || 5.0
        };
        setBusiness(businessData);
        setLikeCount(businessData.likes);
        setBusinessRating(businessData.rating);
      }
    } catch (error) {
      console.error('Error loading business data:', error);
    } finally {
      setLoadingBusiness(false);
    }
  };

  const loadProducts = async () => {
    if (!businessId) return;

    setLoadingProducts(true);
    try {
      // Fetch products for this business from Firestore
      const productsRef = collection(db, 'businesses', businessId, 'products');
      const q = query(productsRef);
      const querySnapshot = await getDocs(q);

      const productsData: Product[] = [];
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        productsData.push({
          id: doc.id,
          name: data.name || '',
          description: data.description || '',
          price: data.price || 0,
          imageUrl: data.imageUrl || '',
          businessId: data.businessId || businessId,
          createdAt: data.createdAt?.toDate() || new Date(),
          updatedAt: data.updatedAt?.toDate() || new Date()
        });
      });

      setProducts(productsData);
    } catch (error) {
      console.error('Error loading products:', error);
    } finally {
      setLoadingProducts(false);
    }
  };

  const loadLikeStatus = async () => {
    if (!businessId) return;

    try {
      const response = await fetch(`/api/businesses/${businessId}/like-status`);
      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.isLiked);
        setLikeCount(data.likes);
        setBusinessRating(data.rating);
      }
    } catch (error) {
      console.error('Error loading like status:', error);
    }
  };

  const toggleLike = async () => {
    if (!businessId) return;

    try {
      const response = await fetch(`/api/businesses/${businessId}/like`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setIsLiked(data.isLiked);
        setLikeCount(data.likes);
        setBusinessRating(data.rating);
      }
    } catch (error) {
      console.error('Error toggling like:', error);
    }
  };

  const openProductDetail = (product: Product) => {
    setSelectedProduct(product);
    setShowProductDetail(true);
  };

  const closeProductDetail = () => {
    setShowProductDetail(false);
    setSelectedProduct(null);
  };

  if (loadingBusiness || !business) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.6)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
        backdropFilter: 'blur(10px)'
      }}>
        <div style={{
          backgroundColor: 'white',
          borderRadius: '20px',
          padding: '40px',
          textAlign: 'center',
          boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
        }}>
          <div style={{
            width: '40px',
            height: '40px',
            border: '3px solid #f3f3f3',
            borderTop: '3px solid #007aff',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
            margin: '0 auto 16px'
          }}></div>
          Loading business details...
        </div>
      </div>
    );
  }

  const categoryInfo = BusinessModel.getCategoryInfo(business.category);

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.6)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
      zIndex: 1000,
      backdropFilter: 'blur(10px)',
      paddingTop: '20px',
      overflowY: 'auto'
    }}>
      <div style={{
        backgroundColor: 'white',
        borderRadius: '20px',
        padding: '24px',
        maxWidth: '500px',
        width: '90%',
        maxHeight: '90vh',
        overflowY: 'auto',
        boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
        marginBottom: '20px'
      }}>
        {/* Header */}
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '20px'
        }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px'
          }}>
            <div style={{
              fontSize: '32px',
              filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.1))'
            }}>
              {categoryInfo.emoji}
            </div>
            <div>
              <h2 style={{
                margin: 0,
                color: '#007aff',
                fontSize: '20px',
                fontWeight: 'bold'
              }}>
                {business.name}
              </h2>
              <h3 style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#666', fontWeight: '600' }}>
                {business.category}
              </h3>
            </div>
          </div>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              fontSize: '28px',
              cursor: 'pointer',
              padding: '8px',
              borderRadius: '50%',
              width: '40px',
              height: '40px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f0f0f0';
              e.currentTarget.style.color = '#333';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#666';
            }}
          >
            ×
          </button>
        </div>

        {/* Business Image with Category Badge */}
        <div style={{ position: 'relative', marginBottom: '20px' }}>
          {business.imageUrl ? (
            <div style={{
              width: '100%',
              borderRadius: '16px',
              overflow: 'hidden',
              position: 'relative',
              backgroundColor: '#f0f0f0',
              boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
              aspectRatio: '16/9',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              border: '3px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <img
                src={business.imageUrl}
                alt={business.name}
                style={{
                  width: '100%',
                  height: '100%',
                  objectFit: 'cover',
                  objectPosition: 'center',
                  display: 'block'
                }}
              />
            </div>
          ) : (
            <div style={{
              width: '100%',
              height: '220px',
              backgroundColor: '#f0f0f0',
              borderRadius: '16px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: '0 8px 20px rgba(0,0,0,0.1)',
              border: '3px solid rgba(255,255,255,0.3)',
              backdropFilter: 'blur(10px)'
            }}>
              <span style={{ fontSize: '60px', color: '#999' }}>🏢</span>
            </div>
          )}

          {/* Category Badge */}
          <div style={{
            position: 'absolute',
            bottom: '16px',
            left: '16px',
            backgroundColor: '#007aff',
            borderRadius: '50%',
            width: '48px',
            height: '48px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)'
          }}>
            {categoryInfo.emoji}
          </div>
        </div>

        {/* Business Info */}
        <div style={{ marginBottom: '20px' }}>
          <div style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            marginBottom: '12px'
          }}>
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '4px'
              }}>
                <span>⭐</span>
                <span style={{ fontSize: '16px', fontWeight: '600' }}>
                  {businessRating.toFixed(1)}
                </span>
              </div>
              {likeCount > 0 && (
                <>
                  <span style={{ color: '#999' }}>•</span>
                  <span style={{ color: '#999' }}>
                    {likeCount} {likeCount === 1 ? 'like' : 'likes'}
                  </span>
                </>
              )}
            </div>

            <button
              onClick={toggleLike}
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                padding: '8px',
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                transition: 'all 0.2s ease'
              }}
            >
              <span style={{
                fontSize: '20px',
                color: isLiked ? '#ff4444' : '#ccc'
              }}>
                {isLiked ? '❤️' : '🤍'}
              </span>
            </button>
          </div>

          {business.description && (
            <p style={{
              margin: 0,
              fontSize: '15px',
              color: '#666',
              lineHeight: '1.5',
              backgroundColor: '#f8f9fa',
              padding: '16px',
              borderRadius: '12px',
              borderLeft: '4px solid #007aff'
            }}>
              {business.description}
            </p>
          )}
        </div>

        {/* Products Section */}
        <div style={{ marginBottom: '20px' }}>
          <h3 style={{
            margin: '0 0 16px 0',
            fontSize: '18px',
            fontWeight: 'bold',
            color: '#333'
          }}>
            Our Products
          </h3>

          {loadingProducts ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#666'
            }}>
              <div style={{
                width: '40px',
                height: '40px',
                border: '3px solid #f3f3f3',
                borderTop: '3px solid #007aff',
                borderRadius: '50%',
                animation: 'spin 1s linear infinite',
                margin: '0 auto 16px'
              }}></div>
              Loading products...
            </div>
          ) : products.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '40px 20px',
              color: '#999'
            }}>
              <span style={{ fontSize: '48px', marginBottom: '16px', display: 'block' }}>🛍️</span>
              No products available
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(150px, 1fr))',
              gap: '16px'
            }}>
              {products.map((product) => (
                <div key={product.id} style={{
                  backgroundColor: 'white',
                  borderRadius: '12px',
                  padding: '12px',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  position: 'relative'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = 'translateY(-2px)';
                  e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                }}
                onClick={() => openProductDetail(product)}
                >
                  {product.imageUrl ? (
                    <div style={{
                      width: '100%',
                      height: '120px',
                      borderRadius: '8px',
                      overflow: 'hidden',
                      position: 'relative',
                      backgroundColor: '#f0f0f0',
                      marginBottom: '8px'
                    }}>
                      <img
                        src={product.imageUrl}
                        alt={product.name}
                        style={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                          objectPosition: 'center',
                          display: 'block'
                        }}
                      />
                    </div>
                  ) : (
                    <div style={{
                      width: '100%',
                      height: '120px',
                      backgroundColor: '#f0f0f0',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      marginBottom: '8px',
                      fontSize: '24px',
                      color: '#999'
                    }}>
                      📦
                    </div>
                  )}

                  <h4 style={{
                    margin: '0 0 4px 0',
                    fontSize: '14px',
                    fontWeight: '600',
                    color: '#333'
                  }}>
                    {product.name}
                  </h4>

                  <p style={{
                    margin: '0 0 8px 0',
                    fontSize: '16px',
                    fontWeight: 'bold',
                    color: '#007aff'
                  }}>
                    ${product.price.toFixed(2)}
                  </p>

                  {product.description && (
                    <p style={{
                      margin: 0,
                      fontSize: '12px',
                      color: '#666',
                      lineHeight: '1.3',
                      display: '-webkit-box',
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: 'vertical',
                      overflow: 'hidden'
                    }}>
                      {product.description}
                    </p>
                  )}

                  {/* Click to view hint */}
                  <div style={{
                    position: 'absolute',
                    bottom: '8px',
                    right: '8px',
                    backgroundColor: 'rgba(0,0,0,0.7)',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '10px',
                    opacity: 0.8
                  }}>
                    Tap for details
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div style={{
          display: 'flex',
          gap: '12px',
          marginTop: '20px'
        }}>
          <button
            onClick={() => {
              if (business.phoneNumber) {
                window.open(`tel:${business.phoneNumber}`, '_self');
              }
            }}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: business.phoneNumber ? '#007aff' : '#ccc',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: business.phoneNumber ? 'pointer' : 'not-allowed',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
            disabled={!business.phoneNumber}
            onMouseEnter={(e) => {
              if (business.phoneNumber) {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
          >
            📞 {business.phoneNumber || 'No Phone'}
          </button>
          <button
            onClick={() => {
              // Use the business location coordinates for navigation
              const lat = business.location.latitude;
              const lng = business.location.longitude;

              const url = `https://www.google.com/maps/dir/?api=1&destination=${lat},${lng}`;
              window.open(url, '_blank');
            }}
            style={{
              flex: 1,
              padding: '14px',
              backgroundColor: '#666',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              cursor: 'pointer',
              fontSize: '15px',
              fontWeight: '600',
              transition: 'all 0.2s ease',
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.transform = 'translateY(-2px)';
              e.currentTarget.style.boxShadow = '0 6px 16px rgba(0,0,0,0.15)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.transform = 'translateY(0)';
              e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
            }}
          >
            🗺️ Directions
          </button>
        </div>

        {/* Location Info */}
        <div style={{
          marginTop: '16px',
          fontSize: '12px',
          color: '#999',
          textAlign: 'center'
        }}>
          📍 {business.location.latitude.toFixed(6)}, {business.location.longitude.toFixed(6)}
        </div>
      </div>

      {/* Product Detail View Modal */}
      {selectedProduct && business && (
        <ProductDetailView
          product={selectedProduct}
          business={business}
          isOpen={showProductDetail}
          onClose={closeProductDetail}
        />
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default BusinessDetailView;
